import React, { useState } from "react";
import { motion } from "framer-motion";
import { LandingNavbar } from "@/components/landing-navbar";
import { Footer } from "@/components/footer";
import { SEO, schemas } from "@/components/SEO";
import { CategoryCard } from "./components/CategoryCard";
import { ArticleCard } from "./components/ArticleCard";
import { SearchBar } from "./components/SearchBar";
import { allHubArticles, HubArticle } from "./data/articles";
import { Rocket, Lightbulb, AlertCircle, BookOpen, HelpCircle, TrendingUp, Mail, X, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const categories = [
  {
    title: "Bidding & Estimating Guides",
    description:
      "Master ISSA 612 standard production rates, square-footage pricing formulas, and winning proposal structures.",
    icon: Rocket,
    articleCount: 8,
    href: "/success-hub/getting-started",
    color: "blue" as const,
  },
  {
    title: "Sales & Lead Generation",
    description:
      "Proven outbound channels, cold outreach scripts for facility managers, and proposal closing playbooks.",
    icon: TrendingUp,
    articleCount: 8,
    href: "/success-hub/sales-lead-gen",
    color: "indigo" as const,
  },
  {
    title: "Operational SOPs & Best Practices",
    description:
      "Proven SOPs for client onboarding, crew scheduling, route density, and facility access protocols.",
    icon: Lightbulb,
    articleCount: 12,
    href: "/success-hub/best-practices",
    color: "green" as const,
  },
  {
    title: "Staffing & Subcontractor Compliance",
    description:
      "Legal and operational frameworks for 1099 subcontractors, COI insurance tracking, and crew management.",
    icon: BookOpen,
    articleCount: 8,
    href: "/success-hub/staff-compliance",
    color: "purple" as const,
  },
  {
    title: "Quality Control & Retention",
    description:
      "Inspection checklists, complaint resolution SOPs, and retention playbooks to prevent client churn.",
    icon: AlertCircle,
    articleCount: 8,
    href: "/success-hub/quality-retention",
    color: "orange" as const,
  },
  {
    title: "Operations & Software FAQ",
    description:
      "Answers to top questions regarding commercial cleaning benchmarks, labor burdens, and platform capabilities.",
    icon: HelpCircle,
    articleCount: 20,
    href: "/success-hub/faq",
    color: "pink" as const,
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};

export default function SuccessHubPage() {
  const [searchQuery, setSearchQuery] = useState("");

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const isSearching = searchQuery.trim().length > 0;
  const lowerQuery = searchQuery.toLowerCase().trim();

  const matchingArticles = isSearching
    ? allHubArticles.filter(
        (article) =>
          article.title.toLowerCase().includes(lowerQuery) ||
          article.excerpt.toLowerCase().includes(lowerQuery) ||
          article.category.toLowerCase().includes(lowerQuery) ||
          article.keywords.some((k) => k.toLowerCase().includes(lowerQuery))
      )
    : [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEO
        title="Success Hub | Commercial Cleaning Operations & Growth Playbooks"
        description="Master commercial cleaning bidding formulas, outbound lead generation, subcontractor agreements, and quality control SOPs."
        keywords={["commercial cleaning guides", "janitorial SOPs", "cleaning bidding formulas", "cleaning sales scripts"]}
        url="/success-hub"
        structuredData={[
          schemas.webSite,
          schemas.createBreadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Success Hub", url: "/success-hub" },
          ]),
        ]}
      />
      <LandingNavbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden pb-16 pt-32">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.08),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(99,102,241,0.06),transparent_50%)]" />

        <div className="container relative mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center"
          >
            <span className="mb-6 inline-flex items-center gap-2 rounded-full bg-blue-100 px-4 py-1.5 text-sm font-semibold text-blue-700">
              <BookOpen className="h-4 w-4" />
              Operations Playbooks & Growth Guides
            </span>
            <h1 className="mb-4 text-4xl font-extrabold text-slate-900 md:text-5xl">
              Grow, Bid & Scale Your Cleaning Business
            </h1>
            <p className="mx-auto max-w-2xl text-lg text-slate-600">
              In-depth operational playbooks on ISSA 612 bidding formulas, facility manager outreach, subcontractor compliance, and quality control SOPs.
            </p>
          </motion.div>

          <SearchBar value={searchQuery} onSearch={handleSearch} />
        </div>
      </section>

      {/* Dynamic Content: Search Results or Category Pillars */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          {isSearching ? (
            <div className="mx-auto max-w-4xl">
              <div className="mb-8 flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 pb-4">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">
                    Search Results for &ldquo;{searchQuery}&rdquo;
                  </h2>
                  <p className="text-sm text-slate-500">
                    Found {matchingArticles.length} operational {matchingArticles.length === 1 ? "guide" : "guides"}
                  </p>
                </div>
                <button
                  onClick={() => setSearchQuery("")}
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-1.5 text-sm font-medium text-slate-600 shadow-sm transition-colors hover:bg-slate-50 hover:text-slate-900"
                >
                  <X className="h-4 w-4" /> Clear Search
                </button>
              </div>

              {matchingArticles.length > 0 ? (
                <div className="space-y-4">
                  {matchingArticles.map((article) => (
                    <ArticleCard
                      key={article.slug}
                      title={article.title}
                      excerpt={article.excerpt}
                      category={article.category}
                      categoryColor={article.categoryColor}
                      readTime={article.readTime}
                      href={`/success-hub/article/${article.slug}`}
                    />
                  ))}
                </div>
              ) : (
                <div className="rounded-3xl border border-dashed border-slate-200 bg-white p-12 text-center shadow-sm">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-50 text-amber-600">
                    <Sparkles className="h-7 w-7" />
                  </div>
                  <h3 className="mt-4 text-xl font-bold text-slate-900">No playbooks found</h3>
                  <p className="mx-auto mt-2 max-w-md text-slate-600">
                    We couldn&apos;t find any guides matching &ldquo;{searchQuery}&rdquo;. Try searching for terms like:
                  </p>
                  <div className="mt-6 flex flex-wrap justify-center gap-2">
                    {["Bidding Formula", "Cold Calling", "Subcontractor 1099", "Inspection SOP", "ISSA Rates"].map((tag) => (
                      <button
                        key={tag}
                        onClick={() => setSearchQuery(tag)}
                        className="rounded-full bg-slate-100 px-4 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-blue-50 hover:text-blue-600"
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <motion.div variants={containerVariants} initial="hidden" animate="visible">
              <div className="mx-auto grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {categories.map((category) => (
                  <motion.div key={category.title} variants={itemVariants as any}>
                    <CategoryCard {...category} />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Quick Help Section */}
      <section className="bg-slate-50 py-16">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="mb-4 text-2xl font-bold text-slate-900">Still need help?</h2>
            <p className="mb-6 text-slate-600">
              Our support team is available Monday through Friday, 9am to 6pm EST. We typically
              respond within 24 hours.
            </p>
            <a
              href="mailto:support@manageclean.com"
              className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-3 font-medium text-white transition-colors hover:bg-blue-700"
            >
              <Mail className="h-5 w-5" />
              Contact Support
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
